import type { ComponentProps } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { FRAMEIO_SUPPORT_EMAIL } from '@frameio/utils/constants';
import { BILLING_URL, PLAN_URL, ROOT_ROUTE, USAGE_URL } from '../constants';
import TEXT from './generated/TEXT/getAccountLockTypeVariant';
import styles from './getAccountLockTypeVariant.module.css';

const StyledLink = ({ className, ...props }: ComponentProps<typeof Link>) => (
  <Link {...props} className={clsx(styles.link, className)} />
);

TEXT.DEFINE(
  {
    /** Contact message for Adobe-managed accounts */
    contactAdminAdobe: 'Contact your admin for assistance.',

    /** Contact message for share access issues */
    shareContactOwner:
      'Please contact the account owner for help with accessing this Share.',

    /** Message for accounts over plan limits beyond grace period (can manage billing) */
    overPlanLimitCanManage:
      'This account has exceeded plan limits beyond the grace period. Reduce usage or upgrade to unlock.',
    /** First line for accounts over plan limits beyond grace period (cannot manage billing) */
    overPlanLimitCannotManageFirstLine:
      'This account has exceeded plan limits beyond the grace period.',
    /** Contact owner message for general assistance */
    contactOwnerGeneral: (ownerEmail: string) => (
      <>
        Contact the account owner
        <StyledLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </StyledLink>
        for assistance.
      </>
    ),

    /** Message for free tier accounts over limits (can manage billing) */
    overFreePlanLimitCanManage:
      'This account has exceeded plan usage limits beyond the grace period. Reduce usage or upgrade to unlock.',

    /** Message for payment failure over plan limits (can manage billing) */
    paymentFailedOverLimitCanManage:
      'Our last payment attempts were unsuccessful. To unlock account, update payment details to resolve overdue balance.',
    /** Contact owner message for payment problem resolution */
    contactOwnerPaymentProblem: (ownerEmail: string) => (
      <>
        Contact the account owner
        <StyledLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </StyledLink>
        for assistance resolving payment problem.
      </>
    ),

    /** Message for payment failure within plan limits (can manage billing) */
    paymentFailedWithinLimitCanManage:
      'Our last payment attempts were unsuccessful. Update payment method to try again.',

    /** Contact support message for manual locks */
    contactSupport: 'Contact Support for assistance.',

    /** Button text for reviewing usage */
    buttonReviewUsage: 'Review Usage',
    /** Button text for comparing plans */
    buttonComparePlans: 'Compare Plans',
    /** Button text for updating payment details */
    buttonUpdatePaymentDetails: 'Update Payment Details',
    /** Button text for updating payment method */
    buttonUpdatePaymentMethod: 'Update Payment Method',
    /** Button text for returning to Frame.io */
    buttonBackToFrameio: 'Back To Frame.io',
    /** Button text for contacting support */
    buttonContactSupport: 'Contact Support',
  },
  { StyledLink }
);

export const getAccountLockTypeVariant = ({
  tier,
  delinquentAt,
  unpaidAt,
  ownerEmail,
  canManageBilling,
  isShare,
  managedByAdobe = false,
}: {
  tier?: string | null;
  delinquentAt?: string | null;
  unpaidAt?: string | null;
  ownerEmail: string;
  canManageBilling: boolean;
  isShare?: boolean;
  managedByAdobe?: boolean;
}) => {
  const BUTTON_OPTIONS = {
    REVIEW: {
      name: TEXT.buttonReviewUsage,
      href: USAGE_URL,
      type: 'route',
    },
    COMPARE: {
      name: TEXT.buttonComparePlans,
      href: PLAN_URL,
      type: 'route',
    },
    BILLING_DETAILS: {
      name: TEXT.buttonUpdatePaymentDetails,
      href: BILLING_URL,
      type: 'route',
    },
    BILLING_METHOD: {
      name: TEXT.buttonUpdatePaymentMethod,
      href: BILLING_URL,
      type: 'route',
    },
    HOME: {
      name: TEXT.buttonBackToFrameio,
      href: ROOT_ROUTE,
      type: 'route',
    },
    CONTACT: {
      name: TEXT.buttonContactSupport,
      href: `mailto:${FRAMEIO_SUPPORT_EMAIL}`,
      type: 'intercom',
    },
  };

  const isEnterprise = tier === 'ENTERPRISE';
  const isFree = tier === 'FREE';
  const isRetail = !isEnterprise;
  const isRetailAndNotFree = !isEnterprise && !isFree;
  const isDelinquent = !!delinquentAt;
  const isUnpaid = !!unpaidAt;

  const isOverPlanLimitBeyondGrace = isRetail && isDelinquent && !isUnpaid;
  const isOverFreePlanLimit = isFree && isDelinquent && isUnpaid;
  const isPaymentFailedOverPlanLimit =
    isRetailAndNotFree && isDelinquent && isUnpaid;
  const isPaymentFailedWithinPlanLimit = isRetail && isUnpaid && !isDelinquent;
  const isManualLockViaSupportTool =
    (isRetail || isEnterprise) && !isDelinquent && !isUnpaid;

  if (managedByAdobe && !canManageBilling) {
    return {
      details: [TEXT.contactAdminAdobe],
      ctas: [BUTTON_OPTIONS.CONTACT],
    };
  }
  if (isShare) {
    return {
      details: [TEXT.shareContactOwner],
      ctas: [BUTTON_OPTIONS.HOME],
    };
  }
  if (isOverPlanLimitBeyondGrace) {
    return {
      details: canManageBilling
        ? [TEXT.overPlanLimitCanManage]
        : [
            TEXT.overPlanLimitCannotManageFirstLine,
            TEXT.contactOwnerGeneral(ownerEmail),
          ],
      ctas: [BUTTON_OPTIONS.REVIEW, BUTTON_OPTIONS.CONTACT],
    };
  }
  if (isOverFreePlanLimit) {
    return {
      details: canManageBilling
        ? [TEXT.overFreePlanLimitCanManage]
        : [
            TEXT.overPlanLimitCannotManageFirstLine,
            TEXT.contactOwnerGeneral(ownerEmail),
          ],
      ctas: [BUTTON_OPTIONS.REVIEW, BUTTON_OPTIONS.COMPARE],
    };
  }
  if (isPaymentFailedOverPlanLimit) {
    return {
      details: canManageBilling
        ? [TEXT.paymentFailedOverLimitCanManage]
        : [TEXT.contactOwnerPaymentProblem(ownerEmail)],
      ctas: [BUTTON_OPTIONS.BILLING_DETAILS],
    };
  }

  if (isPaymentFailedWithinPlanLimit) {
    return {
      details: canManageBilling
        ? [TEXT.paymentFailedWithinLimitCanManage]
        : [TEXT.contactOwnerPaymentProblem(ownerEmail)],
      ctas: [BUTTON_OPTIONS.BILLING_METHOD],
    };
  }

  if (isManualLockViaSupportTool) {
    let details;
    if (canManageBilling) {
      details = [TEXT.contactSupport];
    } else if (managedByAdobe) {
      details = [TEXT.contactAdminAdobe];
    } else {
      details = [TEXT.contactOwnerGeneral(ownerEmail)];
    }

    return {
      details,
      ctas: [BUTTON_OPTIONS.CONTACT],
    };
  }
  if (isUnpaid) {
    return {
      details: canManageBilling
        ? [TEXT.paymentFailedWithinLimitCanManage]
        : [TEXT.contactOwnerPaymentProblem(ownerEmail)],
      ctas: [BUTTON_OPTIONS.BILLING_METHOD],
    };
  }

  // Fallback - same as manual lock
  let details;
  if (canManageBilling) {
    details = [TEXT.contactSupport];
  } else if (managedByAdobe) {
    details = [TEXT.contactAdminAdobe];
  } else {
    details = [TEXT.contactOwnerGeneral(ownerEmail)];
  }

  return {
    details,
    ctas: [BUTTON_OPTIONS.CONTACT],
  };
};
